import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { DocumentSerial, LineSerialsRequest, SerialDetail, SerialPage, SerialRow, SerialSearchFilter } from '../interfaces/inventory/serial.interface';

/** F6b: serial lookup and picker list (all STOCK_VIEW). */
export const SerialService = {
  search: async (institutionId: string, f: SerialSearchFilter): Promise<SerialPage> =>
    (await axios.get(ENDPOINTS.SERIAL.SEARCH(institutionId), {
      params: {
        ...(f.q ? { q: f.q } : {}),
        ...(f.itemId ? { itemId: f.itemId } : {}),
        ...(f.storeId ? { storeId: f.storeId } : {}),
        ...(f.status ? { status: f.status } : {}),
        page: f.page ?? 0,
        size: f.size ?? 50,
      },
    })).data,
  available: async (institutionId: string, storeId: string, itemId: string): Promise<SerialRow[]> =>
    (await axios.get(ENDPOINTS.SERIAL.AVAILABLE(institutionId), { params: { storeId, itemId } })).data,
  /** SOLD serials of an item delivered on a challan (sales-return picker). */
  soldOnChallan: async (institutionId: string, challanId: string, itemId: string): Promise<SerialRow[]> =>
    (await axios.get(ENDPOINTS.SERIAL.SOLD_ON_CHALLAN(institutionId), { params: { challanId, itemId } })).data,
  /** Which of `itemIds` are serial-tracked (empty input -> no call). */
  trackedItems: async (institutionId: string, itemIds: string[]): Promise<string[]> =>
    itemIds.length === 0 ? [] : (await axios.get(ENDPOINTS.SERIAL.TRACKED_ITEMS(institutionId), {
      params: { itemIds: itemIds.join(',') },
    })).data,
  /** Serials a posted document moved: SALE_DELIVERY for a challan, TRANSFER_OUT for a transfer. */
  byDocument: async (institutionId: string, sourceId: string,
    movement: 'SALE_DELIVERY' | 'TRANSFER_OUT'): Promise<DocumentSerial[]> =>
    (await axios.get(ENDPOINTS.SERIAL.BY_DOCUMENT(institutionId), { params: { sourceId, movement } })).data,
  detail: async (institutionId: string, id: string): Promise<SerialDetail> =>
    (await axios.get(ENDPOINTS.SERIAL.DETAIL(institutionId, id))).data,
};

/** Splits pasted/typed text into trimmed, non-empty serial numbers (newline, comma or semicolon separated). */
export const parseSerials = (text: string): string[] =>
  text.split(/[\n,;]+/).map((s) => s.trim()).filter((s) => s.length > 0);

/** Builds the optional post body; undefined when no line carries serials. */
export const toLineSerials = (byLine: Record<string, string[] | undefined>): LineSerialsRequest | undefined => {
  const serials: Record<string, string[]> = {};
  Object.entries(byLine).forEach(([lineId, list]) => {
    if (list && list.length > 0) serials[lineId] = list;
  });
  return Object.keys(serials).length > 0 ? { serials } : undefined;
};

/** Groups a document's serials by item id, so each line can list the serials of its item. */
export const serialsByItem = (rows: DocumentSerial[]): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  rows.forEach((r) => { (out[r.itemId] = out[r.itemId] ?? []).push(r.serialNo); });
  return out;
};
